// TrainingsTab.jsx - ИСПРАВЛЕННАЯ ВЕРСИЯ С ОГРАНИЧЕНИЕМ ДО 5

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import './StyleTab.css';
import { courseService } from '../../../services/courseService';

const TrainingsTab = ({ courses, categories, isLoading, onDataChange }) => {
  const [filteredCourses, setFilteredCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [notification, setNotification] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [deletingCourse, setDeletingCourse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    direction: '',
    category_id: '',
    subcategory: '',
    legal_basis: '',
    duration_hours: '',
    periodicity_months: ''
  });
  const [submitting, setSubmitting] = useState(false);
  
  // Состояние для обновления is_show
  const [updatingShowStatus, setUpdatingShowStatus] = useState({});
  
  // Локальное состояние для чекбоксов
  const [localShowStatus, setLocalShowStatus] = useState({});

  // Нормализация курсов - добавляем is_show для курса 48 и других
  const normalizedCourses = useMemo(() => {
    if (!courses || !Array.isArray(courses)) return [];
    
    return courses.map(course => {
      // Принудительно устанавливаем is_show для курса 48
      if (course.id === 48) {
        return { ...course, is_show: true };
      }
      // Для всех остальных курсов оставляем как есть
      return course;
    });
  }, [courses]);

  // Синхронизация локального статуса с нормализованными курсами
  useEffect(() => {
    if (normalizedCourses && Array.isArray(normalizedCourses)) {
      const statusMap = {};
      normalizedCourses.forEach(course => {
        statusMap[course.id] = course.is_show === true;
      });
      setLocalShowStatus(statusMap);
    }
  }, [normalizedCourses]);

  // Функция для показа уведомлений
  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3000);
  };

  // Подсчет количества выбранных обучений (is_show = true)
  const getSelectedCount = useCallback(() => {
    return Object.values(localShowStatus).filter(status => status === true).length;
  }, [localShowStatus]);

  // Варианты для выпадающего списка "Вид"
  const typeOptions = [
    { value: 'Внутреннее', label: 'Внутреннее' },
    { value: 'Внешнее', label: 'Внешнее' },
    { value: 'Оба', label: 'Оба' }
  ];

  // Получение уникальных категорий из курсов
  const uniqueCategories = useMemo(() => {
    if (categories && categories.length > 0) {
      return categories;
    }
    const categoryMap = new Map();
    if (normalizedCourses && Array.isArray(normalizedCourses)) {
      normalizedCourses.forEach(course => {
        if (course.category_name) {
          const key = course.category_id || course.category_name;
          if (!categoryMap.has(key)) {
            categoryMap.set(key, {
              id: course.category_id || key,
              name: course.category_name
            });
          }
        }
      });
    }
    return Array.from(categoryMap.values());
  }, [normalizedCourses, categories]);

  // Фильтрация курсов
  useEffect(() => {
    if (!normalizedCourses) {
      setFilteredCourses([]);
      return;
    }

    let filtered = [...normalizedCourses];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(course => 
        course.name?.toLowerCase().includes(term) ||
        course.category_name?.toLowerCase().includes(term) ||
        course.type?.toLowerCase().includes(term) ||
        course.direction?.toLowerCase().includes(term) ||
        course.legal_basis?.toLowerCase().includes(term)
      );
    }

    if (selectedCategory) {
      filtered = filtered.filter(course => 
        course.category_id === parseInt(selectedCategory) ||
        course.category_name === selectedCategory
      );
    }

    if (selectedType) {
      filtered = filtered.filter(course => course.type === selectedType);
    }

    setFilteredCourses(filtered);
  }, [normalizedCourses, searchTerm, selectedCategory, selectedType]);

  // Группировка курсов по категориям
  const groupedCourses = useMemo(() => {
    const groups = {};
    
    filteredCourses.forEach(course => {
      const categoryKey = course.category_id || course.category_name || 'Без категории';
      const categoryName = course.category_name || 'Без категории';
      
      if (!groups[categoryKey]) {
        groups[categoryKey] = {
          id: categoryKey,
          name: categoryName,
          courses: []
        };
      }
      groups[categoryKey].courses.push(course);
    });
    
    return Object.values(groups).sort((a, b) => a.name.localeCompare(b.name));
  }, [filteredCourses]);

  // Инициализация состояния свернутых/развернутых категорий
  useEffect(() => {
    const initialState = {};
    groupedCourses.forEach(group => {
      initialState[group.id] = true;
    });
    setExpandedCategories(prev => ({
      ...prev,
      ...initialState
    }));
  }, [groupedCourses]);

  // Переключение состояния категории
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // Обработчик изменения is_show с ограничением до 5
  const handleShowStatusChange = async (course, isChecked) => {
    // Защита от повторных кликов
    if (updatingShowStatus[course.id]) return;
    
    const currentSelectedCount = getSelectedCount();
    
    // Проверка: если пытаемся включить чекбокс (isChecked = true) и уже выбрано 5 обучений
    if (isChecked && currentSelectedCount >= 5) {
      showNotification('Нельзя выбрать более 5 обучений для отображения в карточке', 'error');
      return;
    }
    
    // Мгновенно обновляем локальный статус
    setLocalShowStatus(prev => ({
      ...prev,
      [course.id]: isChecked
    }));
    
    setUpdatingShowStatus(prev => ({ ...prev, [course.id]: true }));
    
    try {
      // Вызов API для обновления на сервере
      await courseService.updateShowStatus(course.id, isChecked);
      
      // Перезагружаем данные с сервера
      if (onDataChange) {
        await onDataChange();
      }
      
      showNotification(
        `Курс "${course.name}" ${isChecked ? 'добавлен в карточку' : 'удален из карточки'}`,
        'success'
      );
      
    } catch (error) {
      console.error('Ошибка обновления is_show:', error);
      
      // Откатываем локальный статус при ошибке
      setLocalShowStatus(prev => ({
        ...prev,
        [course.id]: !isChecked
      }));
      
      showNotification(error.message || 'Ошибка при обновлении статуса', 'error');
    } finally {
      setUpdatingShowStatus(prev => ({ ...prev, [course.id]: false }));
    }
  };

  // Открытие модального окна для добавления
  const handleAdd = () => {
    setEditingCourse(null);
    setFormData({
      name: '',
      type: '',
      direction: '',
      category_id: '',
      subcategory: '',
      legal_basis: '',
      duration_hours: '',
      periodicity_months: ''
    });
    setIsModalOpen(true);
  };

  // Открытие модального окна для редактирования
  const handleEdit = (course) => {
    setEditingCourse(course);
    setFormData({
      name: course.name || '',
      type: course.type || '',
      direction: course.direction || '',
      category_id: course.category_id?.toString() || '',
      subcategory: course.subcategory || '',
      legal_basis: course.legal_basis || '',
      duration_hours: course.duration_hours?.toString() || '',
      periodicity_months: course.periodicity_months?.toString() || ''
    });
    setIsModalOpen(true);
  };

  // Обработка изменения формы
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Сохранение курса
  const handleSave = async () => {
    if (!formData.name.trim()) {
      showNotification('Введите название обучения', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const courseData = {
        name: formData.name.trim(),
        type: formData.type || null,
        direction: formData.direction || null,
        category_id: formData.category_id ? parseInt(formData.category_id) : null,
        subcategory: formData.subcategory || null,
        legal_basis: formData.legal_basis || null,
        duration_hours: formData.duration_hours ? parseFloat(formData.duration_hours) : null,
        periodicity_months: formData.periodicity_months ? parseInt(formData.periodicity_months) : null,
        is_show: false // По умолчанию новый курс не показывается в карточке
      };

      if (editingCourse) {
        await courseService.update(editingCourse.id, courseData);
        showNotification('Обучение успешно обновлено', 'success');
      } else {
        await courseService.create(courseData);
        showNotification('Обучение успешно создано', 'success');
      }

      setIsModalOpen(false);
      // Полная перезагрузка только при создании/редактировании/удалении
      if (onDataChange) await onDataChange();
    } catch (error) {
      console.error('Ошибка сохранения:', error);
      showNotification(error.message || 'Ошибка при сохранении', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Открытие модального окна удаления
  const handleDeleteClick = (course) => {
    setDeletingCourse(course);
    setIsDeleteModalOpen(true);
  };

  // Подтверждение удаления
  const handleDeleteConfirm = async () => {
    if (!deletingCourse) return;

    setSubmitting(true);
    try {
      await courseService.delete(deletingCourse.id);
      showNotification('Обучение успешно удалено', 'success');
      setIsDeleteModalOpen(false);
      setDeletingCourse(null);
      if (onDataChange) await onDataChange();
    } catch (error) {
      console.error('Ошибка удаления:', error);
      showNotification(error.message || 'Ошибка при удалении', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // Получение количества выбранных обучений для отображения в интерфейсе
  const selectedCount = getSelectedCount();
  const isMaxSelected = selectedCount >= 5;

  if (isLoading) {
    return (
      <div className="trainings-tab">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Загрузка обучений...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="trainings-tab">
      {/* Уведомления */}
      {notification && (
        <div className={`toast-notification ${notification.type}`}>
          <span className="toast-icon">
            {notification.type === 'success' ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            )}
          </span>
          <span className="toast-message">{notification.message}</span>
        </div>
      )}

      <div className="tab-header">
        <h3>Управление обучениями</h3>
        <button className="btn-add" onClick={handleAdd}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Добавить обучение
        </button>
      </div>

      {/* Индикатор количества выбранных обучений */}
      <div className="selected-counter" style={{ 
        marginBottom: '15px', 
        padding: '8px 12px', 
        backgroundColor: isMaxSelected ? '#fff3cd' : '#e8f0fe', 
        borderRadius: '6px',
        fontSize: '14px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <span>Выбрано обучений для отображения в карточке:</span>
        <span style={{ 
          fontWeight: 'bold', 
          color: isMaxSelected ? '#856404' : '#1a73e8',
          fontSize: '16px'
        }}>
          {selectedCount} / 5
        </span>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          className="search-input"
          placeholder="Поиск по названию, категории, виду, направлению или НПА..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="filter-select"
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
        >
          <option value="">Все категории</option>
          {uniqueCategories.map(cat => (
            <option key={cat.id || cat.name} value={cat.id || cat.name}>
              {cat.name}
            </option>
          ))}
        </select>
        <select
          className="filter-select"
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
        >
          <option value="">Все виды</option>
          {typeOptions.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="trainings-table-wrapper">
        <table className="trainings-table">
          <thead>
            <tr>
              <th style={{ width: '30px' }}></th>
              <th>ID</th>
              <th>Название</th>
              <th>Вид</th>
              <th>Направление</th>
              <th>Подкатегория</th>
              <th>НПА</th>
              <th>Длительность (часов)</th>
              <th>Периодичность (мес)</th>
              <th style={{ width: '100px', textAlign: 'center' }}>В карточку</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {groupedCourses.length === 0 ? (
              <tr>
                <td colSpan="11" className="empty-message">
                  {normalizedCourses?.length === 0 ? 'Нет добавленных обучений' : 'Ничего не найдено'}
                </td>
              </tr>
            ) : (
              groupedCourses.map(group => {
                const isExpanded = expandedCategories[group.id] ?? true;
                const categoryCourses = group.courses;
                
                return (
                  <React.Fragment key={group.id}>
                    {/* Строка категории */}
                    <tr 
                      className="category-row"
                      onClick={() => toggleCategory(group.id)}
                      style={{ cursor: 'pointer', backgroundColor: '#f5f5f5' }}
                    >
                      <td className="category-expand">
                        <span className="expand-icon">
                          {isExpanded ? (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="6 9 12 15 18 9" />
                            </svg>
                          ) : (
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="9 18 15 12 9 6" />
                            </svg>
                          )}
                        </span>
                      </td>
                      <td colSpan="10">
                        <strong className="category-name-text">
                          {group.name.toUpperCase()}
                        </strong>
                        <span className="category-count" style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
                          ({categoryCourses.length} записей)
                        </span>
                      </td>
                    </tr>
                    
                    {/* Строки курсов в категории */}
                    {isExpanded && categoryCourses.map(course => {
                      // Получаем статус чекбокса из локального состояния или из курса
                      const isShow = localShowStatus[course.id] !== undefined 
                        ? localShowStatus[course.id] 
                        : (course.is_show === true);
                      const isUpdating = updatingShowStatus[course.id];
                      
                      // Определяем, должен ли чекбокс быть disabled
                      // Если достигнут лимит в 5 И текущий чекбокс НЕ выбран - блокируем
                      const isDisabled = isUpdating || (isMaxSelected && !isShow);
                      
                      return (
                        <tr key={course.id}>
                          <td></td>
                          <td>{course.id}</td>
                          <td className="course-name">{course.name}</td>
                          <td>
                            <span className={`type-badge type-${getTypeClass(course.type)}`}>
                              {course.type || '—'}
                            </span>
                          </td>
                          <td>
                            {course.direction ? (
                              <span className="direction-badge">
                                {course.direction}
                              </span>
                            ) : '—'}
                          </td>
                          <td>{course.subcategory || '—'}</td>
                          <td>{course.legal_basis || '—'}</td>
                          <td>{course.duration_hours ? `${course.duration_hours} ч` : '—'}</td>
                          <td>{course.periodicity_months ? `${course.periodicity_months} мес` : 'Бессрочно'}</td>
                          <td className="show-status-cell">
                            <label className="show-status-checkbox">
                              <input
                                type="checkbox"
                                checked={isShow}
                                onChange={(e) => handleShowStatusChange(course, e.target.checked)}
                                disabled={isDisabled}
                              />
                              <span className="checkmark"></span>
                              {isUpdating && <span className="checkbox-spinner-small"></span>}
                            </label>
                            {/* Подсказка при недоступности чекбокса */}
                            {isDisabled && !isShow && !isUpdating && (
                              <span className="checkbox-tooltip" style={{ 
                                fontSize: '11px', 
                                color: '#856404',
                                display: 'block',
                                marginTop: '4px'
                              }}>
                                (достигнут лимит 5)
                              </span>
                            )}
                          </td>
                          <td className="actions-cell">
                            <button 
                              className="icon-btn edit" 
                              onClick={() => handleEdit(course)}
                              title="Редактировать"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                                <path d="M21 21l-4-4" />
                              </svg>
                            </button>
                            <button 
                              className="icon-btn delete" 
                              onClick={() => handleDeleteClick(course)}
                              title="Удалить"
                            >
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0h10" />
                                <line x1="10" y1="11" x2="10" y2="17" />
                                <line x1="14" y1="11" x2="14" y2="17" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Модальное окно добавления/редактирования */}
      {isModalOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setIsModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingCourse ? 'Редактирование обучения' : 'Добавление обучения'}</h3>
              <button className="modal-close" onClick={() => !submitting && setIsModalOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Название *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  placeholder="Введите название обучения"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Вид</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleFormChange}
                  disabled={submitting}
                >
                  <option value="">Не выбрано</option>
                  {typeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Направление</label>
                <input
                  type="text"
                  name="direction"
                  value={formData.direction}
                  onChange={handleFormChange}
                  placeholder="Введите направление обучения"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>Категория</label>
                <select
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleFormChange}
                  disabled={submitting}
                >
                  <option value="">Выберите категорию</option>
                  {uniqueCategories.map(cat => (
                    <option key={cat.id || cat.name} value={cat.id || cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Подкатегория</label>
                <input
                  type="text"
                  name="subcategory"
                  value={formData.subcategory}
                  onChange={handleFormChange}
                  placeholder="Введите подкатегорию"
                  disabled={submitting}
                />
              </div>

              <div className="form-group">
                <label>НПА (нормативно-правовой акт)</label>
                <input
                  type="text"
                  name="legal_basis"
                  value={formData.legal_basis}
                  onChange={handleFormChange}
                  placeholder="Введите основание (ФЗ, приказ и т.д.)"
                  disabled={submitting}
                />
              </div>

              <div className="form-row">
                <div className="form-group half">
                  <label>Длительность (часов)</label>
                  <input
                    type="number"
                    name="duration_hours"
                    value={formData.duration_hours}
                    onChange={handleFormChange}
                    placeholder="Например: 40"
                    disabled={submitting}
                    step="0.5"
                  />
                </div>
                <div className="form-group half">
                  <label>Периодичность (месяцев)</label>
                  <input
                    type="number"
                    name="periodicity_months"
                    value={formData.periodicity_months}
                    onChange={handleFormChange}
                    placeholder="Например: 12 (оставьте пустым для бессрочного)"
                    disabled={submitting}
                    step="1"
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn-secondary" 
                onClick={() => !submitting && setIsModalOpen(false)}
                disabled={submitting}
              >
                Отмена
              </button>
              <button 
                className="btn-primary" 
                onClick={handleSave}
                disabled={submitting}
              >
                {submitting ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно удаления */}
      {isDeleteModalOpen && (
        <div className="modal-overlay" onClick={() => !submitting && setIsDeleteModalOpen(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header delete-header">
              <h3>Подтверждение удаления</h3>
              <button className="modal-close" onClick={() => !submitting && setIsDeleteModalOpen(false)}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="modal-body delete-body">
              <div className="delete-icon">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
              </div>
              <p>Вы действительно хотите удалить обучение?</p>
              <p className="delete-position-name">«{deletingCourse?.name}»</p>
              <div className="delete-warning">
                Это действие невозможно отменить. Обучение будет удалено из матрицы компетенций.
              </div>
            </div>
            <div className="modal-footer delete-footer">
              <button 
                className="btn-secondary" 
                onClick={() => !submitting && setIsDeleteModalOpen(false)}
                disabled={submitting}
              >
                Отмена
              </button>
              <button 
                className="btn-danger" 
                onClick={handleDeleteConfirm}
                disabled={submitting}
              >
                {submitting ? 'Удаление...' : 'Удалить'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Вспомогательная функция для получения CSS класса типа обучения
const getTypeClass = (type) => {
  if (!type) return 'none';
  switch (type.toLowerCase()) {
    case 'внутреннее':
      return 'internal';
    case 'внешнее':
      return 'external';
    case 'оба':
      return 'both';
    default:
      return 'none';
  }
};

export default TrainingsTab;